/**
 * Rendering primitives shared by every section.
 *
 * These are intentionally plain and consistent rather than pixel-accurate: their job
 * is to let regional editors see their content in a sensible layout while the design
 * pass (milestone M2) is still ahead of us. Tailwind handles the responsive behaviour;
 * breakpoint tuning per section comes later.
 */
import type { ReactNode } from 'react';
import { AppLink } from '../components/ui/AppLink';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { Image, SIZES } from '../components/ui/Image';
import type {
  AccordionItem, BlockNode, Card, CountryItem, Cta, Eyebrow as EyebrowValue,
  LinkItem, MediaValue, Person, SectionSettings, Slide, StrapiMedia, TimelineItem
} from '../types/sections';

/* -------------------------------------------------------------------- shell */

const BACKGROUNDS: Record<string, string> = {
  default: 'bg-surface',
  muted: 'bg-surface-muted',
  dark: 'bg-surface-dark text-white [&_h2]:text-white [&_h3]:text-white',
  brand: 'bg-brand-700 text-white [&_h2]:text-white [&_h3]:text-white'
};

const SPACING: Record<string, { top: string; bottom: string }> = {
  none: { top: 'pt-0', bottom: 'pb-0' },
  // 16px: the grid gutter, for blocks the design sets exactly one column-gap apart.
  xs: { top: 'pt-4', bottom: 'pb-4' },
  sm: { top: 'pt-6', bottom: 'pb-6' },
  md: { top: 'pt-12 lg:pt-16', bottom: 'pb-12 lg:pb-16' },
  lg: { top: 'pt-20 lg:pt-28', bottom: 'pb-20 lg:pb-28' }
};

export function SectionShell({
  settings, name, children
}: { settings?: SectionSettings; name: string; children: ReactNode }) {
  const background = BACKGROUNDS[settings?.background ?? 'default'] ?? BACKGROUNDS.default;
  const top = (SPACING[settings?.spacingTop ?? 'md'] ?? SPACING.md).top;
  const bottom = (SPACING[settings?.spacingBottom ?? 'md'] ?? SPACING.md).bottom;
  const fullBleed = settings?.fullBleed;

  return (
    <section
      id={settings?.anchor || undefined}
      data-section={name}
      className={`${background} ${top} ${bottom}`}
    >
      <div className={fullBleed ? 'w-full overflow-x-auto' : 'weco-container'}>
        <div className="flex flex-col gap-6">{children}</div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------- text */

/**
 * The chip above a heading - the file's `section name` component. Its label is
 * `Desktop/Text/caption`, 12px, on every section that has one (6377:88006, 6377:87436).
 */
export function Eyebrow({ value }: { value?: EyebrowValue }) {
  if (!value?.label) return null;
  const content = (
    <span className="inline-flex w-fit rounded-full border border-ink/10 bg-surface px-4 py-1.5 text-caption text-ink">
      {value.label}
    </span>
  );
  return value.url ? <AppLink to={value.url} className="w-fit transition hover:border-ink/30">{content}</AppLink> : content;
}

/**
 * Inline colour in a heading, and the convention for asking for it.
 *
 * The design paints part of some headings in a green-to-blue gradient - "narzedzia
 * online" in `3883:70431`, where the file names the two stops `Color/Green/Green-500`
 * and `Color/Blue/Blue-600`, both already tokens here. This was an open question from
 * M3, because a plain string cannot carry it and making `heading` rich text would buy it
 * at the price of editors putting lists and links inside an `<h2>`.
 *
 * **An editor marks the run with braces**: `Sprawdzone {narzedzia online} dla podrozy
 * sluzbowych`. One convention, used by every heading that goes through here, rather than
 * a field on one section - which is what CLAUDE.md said it would take.
 *
 * The gradient is clipped to the marked run rather than to the whole heading. The file
 * runs its gradient box wider than the coloured words, so its first letter is already a
 * fifth of the way along; matching that exactly would mean modelling a gradient box that
 * extends under black text, for a difference of one shade on one letter.
 *
 * `forced-colors` turns it off: `text-transparent` over a background is invisible in a
 * high-contrast mode, which would silently delete two words from the heading.
 */
const HIGHLIGHT =
  'bg-gradient-to-r from-green-500 to-blue-600 bg-clip-text text-transparent '
  + 'forced-colors:bg-none forced-colors:text-[CanvasText]';

export function withHighlight(text?: string | null): ReactNode {
  if (!text) return text;
  if (!text.includes('{')) return text;

  // Split on the braces and keep the separators, so the marked runs can be wrapped.
  return text.split(/(\{[^{}]*\})/g).map((part, i) =>
    part.startsWith('{') && part.endsWith('}')
      ? <span key={i} className={HIGHLIGHT}>{part.slice(1, -1)}</span>
      : part
  );
}

export function Heading({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <h2 className="max-w-4xl text-h3 font-medium text-ink lg:text-h2">
      {typeof children === 'string' ? withHighlight(children) : children}
    </h2>
  );
}

export function Lead({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return <p className="max-w-3xl text-body-md text-ink-muted">{children}</p>;
}

export function Body({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return <p className="max-w-3xl whitespace-pre-line text-ink-muted">{children}</p>;
}

export function Meta({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return <p className="text-body-sm font-medium text-ink-faint">{children}</p>;
}

/* ------------------------------------------------------- rich text (blocks) */

function renderInline(nodes: BlockNode[] | undefined, keyPrefix: string): ReactNode {
  if (!nodes) return null;
  return nodes.map((node, i) => {
    const key = `${keyPrefix}-${i}`;
    if (node.type === 'link') return <AppLink key={key} to={node.url}>{renderInline(node.children, key)}</AppLink>;
    let out: ReactNode = node.text ?? '';
    if (node.bold) out = <strong key={`${key}-b`}>{out}</strong>;
    if (node.italic) out = <em key={`${key}-i`}>{out}</em>;
    if (node.code) out = <code key={`${key}-c`}>{out}</code>;
    if (node.underline) out = <u key={`${key}-u`}>{out}</u>;
    return <span key={key}>{out}</span>;
  });
}

/** Minimal renderer for Strapi 5 "blocks" rich text. No dependency needed. */
export function Blocks({ value, className }: { value?: BlockNode[]; className?: string }) {
  if (!value?.length) return null;

  return (
    <div className={`weco-prose ${className ?? ''}`}>
      {value.map((block, i) => {
        const key = `b-${i}`;
        switch (block.type) {
          case 'heading': {
            const Tag = (`h${Math.min(Math.max(block.level ?? 2, 2), 4)}`) as 'h2' | 'h3' | 'h4';
            return <Tag key={key}>{renderInline(block.children, key)}</Tag>;
          }
          case 'list': {
            const Tag = block.format === 'ordered' ? 'ol' : 'ul';
            return (
              <Tag key={key}>
                {(block.children ?? []).map((li, j) => <li key={`${key}-${j}`}>{renderInline(li.children, `${key}-${j}`)}</li>)}
              </Tag>
            );
          }
          case 'quote':
            return <blockquote key={key} className="border-l-2 border-brand-300 pl-4 italic">{renderInline(block.children, key)}</blockquote>;
          case 'image':
            return block.image ? (
              <Image key={key} media={block.image} sizes="(min-width: 768px) 768px, 100vw" className="my-4 rounded-s" />
            ) : null;
          default:
            return <p key={key}>{renderInline(block.children, key)}</p>;
        }
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ media */

const ASPECT: Record<string, string> = {
  rectangle: 'aspect-[16/9]', square: 'aspect-square', wide: 'aspect-[21/9]', auto: ''
};

function Img({
  media, alt, className, sizes = SIZES.full
}: { media?: StrapiMedia | null; alt?: string; className?: string; sizes?: string }) {
  return (
    <Image
      media={media}
      alt={alt ?? media?.alternativeText ?? ''}
      sizes={sizes}
      className={`h-full w-full object-cover ${className ?? ''}`}
    />
  );
}

export function MediaBlock({ value }: { value?: MediaValue }) {
  if (!value?.image) return null;
  return (
    <figure className={`overflow-hidden rounded-m bg-surface-muted ${ASPECT[value.aspect ?? 'rectangle'] ?? ''}`}>
      <Img media={value.image} alt={value.alt} />
    </figure>
  );
}

export function MediaRow({ items }: { items?: StrapiMedia[] }) {
  if (!items?.length) return null;
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {items.map((m, i) => (
        <figure key={i} className="aspect-square overflow-hidden rounded-m bg-surface-muted">
          <Img media={m} sizes={SIZES.quarter} />
        </figure>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- actions */

/**
 * A call to action from the CMS.
 *
 * `primary` and `secondary` are the Figma Button component, which carries the trailing
 * arrow the section mock-ups all show. `link` stays a plain text link - the design has
 * no button for it.
 */
export function CtaLink({ value }: { value?: Cta }) {
  if (!value?.label) return null;

  if (value.variant === 'link') {
    // Figma 6377:86970 - blue, arrow, no rule under it until hover.
    return (
      <AppLink
        to={value.url}
        className="group inline-flex w-fit items-center gap-2 text-body text-brand-500 hover:text-brand-600"
      >
        <span className="underline-offset-4 group-hover:underline">{value.label}</span>
        <Icon name="arrow-right" size={18} className="transition group-hover:translate-x-1" />
      </AppLink>
    );
  }

  return (
    <Button to={value.url} variant={value.variant === 'secondary' ? 'secondary' : 'primary'} withIcon>
      {value.label}
    </Button>
  );
}

/**
 * A row of calls to action, with the text links on a line of their own underneath.
 *
 * The submissions panel (`3876:54341`) draws two buttons side by side and an underlined
 * link below them, 36px down. Left in one wrapping row the link tucks in beside the
 * second button whenever there is space, which reads as a third button that lost its
 * fill.
 */
export function CtaRow({ items }: { items?: Cta[] }) {
  if (!items?.length) return null;
  const buttons = items.filter((c) => c.variant !== 'link');
  const links = items.filter((c) => c.variant === 'link');

  return (
    <div className="flex flex-col gap-9">
      {buttons.length ? (
        <div className="flex flex-wrap gap-3">{buttons.map((c, i) => <CtaLink key={i} value={c} />)}</div>
      ) : null}
      {links.length ? (
        <div className="flex flex-wrap gap-6">{links.map((c, i) => <CtaLink key={i} value={c} />)}</div>
      ) : null}
    </div>
  );
}

/**
 * The row of pill links the design puts under several sections - a grey pill with the
 * label on the left and a filled circular arrow on the right (Figma 6377:86971,
 * 6377:86995).
 */
export function LinkPills({ items }: { items?: LinkItem[] }) {
  if (!items?.length) return null;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((item, i) => (
        <AppLink
          key={i}
          to={item.url}
          className="group flex items-center justify-between gap-4 rounded-full bg-surface-muted py-2 pl-6 pr-2 text-body-sm text-ink transition hover:bg-surface-sunken"
        >
          {item.label}
          <Icon name="arrow-circle-right" size={36} className="shrink-0 text-ink-strong transition group-hover:translate-x-0.5" />
        </AppLink>
      ))}
    </div>
  );
}

export function LinkNav({ items }: { items?: LinkItem[] }) {
  if (!items?.length) return null;
  return (
    <nav className="flex flex-col gap-2">
      {items.map((item, i) => (
        <AppLink key={i} to={item.url} className="rounded-s border border-ink/10 px-5 py-3 text-body-sm font-medium hover:border-brand-400 hover:text-brand-600">
          {item.label}
        </AppLink>
      ))}
    </nav>
  );
}

/* ------------------------------------------------------------------ lists */

export function CardGrid({ items }: { items?: Card[] }) {
  if (!items?.length) return null;
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((card, i) => {
        const inner = (
          <>
            {card.image ? <figure className="mb-4 aspect-[4/3] overflow-hidden rounded-s bg-surface-muted"><Img media={card.image} /></figure> : null}
            {card.label ? <span className="mb-3 block text-caption uppercase tracking-widest text-brand-500">{card.label}</span> : null}
            <h3 className="mb-2 text-body-md font-semibold text-ink">{card.title}</h3>
            {card.body ? <p className="text-body-sm text-ink-muted">{card.body}</p> : null}
          </>
        );
        return card.url
          ? <AppLink key={i} to={card.url} className="rounded-m border border-ink/10 p-5 transition hover:border-brand-400">{inner}</AppLink>
          : <article key={i} className="rounded-m border border-ink/10 p-5">{inner}</article>;
      })}
    </div>
  );
}

export function TimelineList({ items }: { items?: TimelineItem[] }) {
  if (!items?.length) return null;
  return (
    <ol className="flex gap-6 pb-4">
      {items.map((item, i) => (
        <li key={i} className="w-72 shrink-0 border-t-2 border-brand-500 pt-4">
          <p className="text-h4 font-semibold text-brand-600">{item.year}</p>
          {item.month ? <p className="text-caption uppercase tracking-widest text-ink-faint">{item.month}</p> : null}
          {item.title ? <h3 className="mt-2 font-semibold text-ink">{item.title}</h3> : null}
          {item.body ? <p className="mt-1 text-body-sm text-ink-muted">{item.body}</p> : null}
          {item.image ? <figure className="mt-3 aspect-[4/3] overflow-hidden rounded-s bg-surface-muted"><Img media={item.image} /></figure> : null}
        </li>
      ))}
    </ol>
  );
}

export function SlideList({ items }: { items?: Slide[] }) {
  if (!items?.length) return null;
  return (
    <div className="flex gap-6 pb-4">
      {items.map((slide, i) => (
        <blockquote key={i} className="w-96 shrink-0 rounded-m bg-surface-muted p-6">
          <p className="text-body-md">{slide.text}</p>
          {slide.author ? <footer className="mt-4 text-body-sm font-medium">{slide.author}{slide.role ? <span className="text-ink-faint">, {slide.role}</span> : null}</footer> : null}
        </blockquote>
      ))}
    </div>
  );
}

export function PersonGrid({ items }: { items?: Person[] }) {
  if (!items?.length) return null;
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((person, i) => (
        <article key={i}>
          <figure className="mb-3 aspect-[3/4] overflow-hidden rounded-m bg-surface-muted"><Img media={person.photo} alt={person.name} /></figure>
          <h3 className="font-semibold text-ink">{person.name}</h3>
          {person.role ? <p className="text-body-sm text-ink-faint">{person.role}</p> : null}
          {person.bio ? <p className="mt-2 text-body-sm text-ink-muted">{person.bio}</p> : null}
        </article>
      ))}
    </div>
  );
}

export function CountryGrid({ items }: { items?: CountryItem[] }) {
  if (!items?.length) return null;
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((country, i) => (
        <article key={i} className="rounded-m border border-ink/10 p-5">
          {country.flag ? <figure className="mb-3 h-8 w-12 overflow-hidden rounded-xs"><Img media={country.flag} alt={country.name} /></figure> : null}
          <h3 className="font-semibold text-ink">{country.name}</h3>
          {country.capital ? <p className="text-body-sm text-ink-faint">{country.capital}</p> : null}
          {country.body ? <p className="mt-2 text-body-sm text-ink-muted">{country.body}</p> : null}
        </article>
      ))}
    </div>
  );
}

export function AccordionList({ items }: { items?: AccordionItem[] }) {
  if (!items?.length) return null;
  return (
    <div className="divide-y divide-ink/10 border-y border-ink/10">
      {items.map((item, i) => (
        <details key={i} className="group py-4">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
            {item.title}
            <span aria-hidden className="text-brand-500 transition group-open:rotate-45">+</span>
          </summary>
          <div className="pt-3"><Blocks value={item.body} /></div>
        </details>
      ))}
    </div>
  );
}
